import { StoreTierControls } from "@/shared/stores/components/subscriptions/StoreTierControls";
import { StoreLifecycleHistory } from "@/shared/stores/components/subscriptions/StoreLifecycleHistory";
import { useStoreTierQuery } from "@/shared/stores/services/queries/useStoreTierQuery";
import type { StoreKind } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import type {
  StoreSubscriptionAssignmentView,
  StoreSubscriptionPaymentView,
  SubscriptionPlanView,
} from "@/lib/api/types";
import {
  STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS,
  SUBSCRIPTION_PLAN_TYPE,
} from "@/lib/api/types";
import { useStoreSubscriptionLedgerQuery } from "@/shared/stores/services/queries/useStoreSubscriptionLedgerQuery";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { formatDate } from "@/lib/formatting/date-time";
import { CircleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { DataTable, EmptyView, Icon, TableEmpty, Timeline, type Column, type TimelineItem } from "@/shared/rd";
import { Meta, SectionCard, SectionHead } from "@/shared/stores/components/rd-local";
import { StoreSubscriptionModal } from "./StoreSubscriptionModal";
import { StorePaymentModal } from "@/shared/stores/components/subscriptions/StorePaymentModal";
import { StoreTopUpRequestsPanel } from "./StoreTopUpRequestsPanel";
import { SubscriptionPaymentProofModal } from "@/shared/stores/components/subscriptions/SubscriptionPaymentProofModal";
import { SubscriptionPaymentReverseButton } from "./SubscriptionPaymentReverseButton";

type Translator = (key: string, values?: Record<string, string | number>) => string;

type StoreSubscriptionsTabProps = {
  locale: Locale;
  storeId: string;
  storeKind?: StoreKind;
  assignments: StoreSubscriptionAssignmentView[];
  payments: StoreSubscriptionPaymentView[];
  plans: SubscriptionPlanView[];
  /** Managing admins only — the reverse action is hidden from everyone else. */
  canReversePayments: boolean;
  t: Translator;
  /** Rendered between the plan / wallet cards and the payments (the mock puts Tag Manager there). */
  afterPlan?: ReactNode;
};

export function StoreSubscriptionsTab({
  locale,
  storeId,
  storeKind = "RESTAURANT",
  assignments,
  payments,
  plans,
  canReversePayments,
  t,
  afterPlan,
}: StoreSubscriptionsTabProps) {
  // Assignments come back newest-first, so the head row is the store's current
  // contract — it is what decides whether payments here are top-ups.
  const isDefault = storeKind === "DEFAULT";
  // Same query (and cache entry) as StoreTierControls; empty id = disabled.
  const tierStatus = useStoreTierQuery(isDefault ? storeId : "");
  const currentAssignment = assignments[0] ?? null;
  const isPerOrderCredit =
    currentAssignment?.type === SUBSCRIPTION_PLAN_TYPE.PER_ORDER_CREDIT;
  const { data: ledger } = useStoreSubscriptionLedgerQuery({
    storeId: isDefault || currentAssignment ? storeId : "",
  });
  // A seller's request is not a payment until an admin confirms it, so the two
  // lists are disjoint: unreviewed and rejected requests sit in the review
  // panel, and everything CONFIRMED — admin-recorded or seller-submitted — is
  // money and belongs in the payments list.
  const topUpRequests = payments.filter(
    (payment) =>
      payment.requestedBySeller !== null &&
      payment.reviewStatus !==
        STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.CONFIRMED,
  );
  const recordedPayments = payments.filter(
    (payment) =>
      payment.reviewStatus ===
      STORE_SUBSCRIPTION_PAYMENT_REVIEW_STATUS.CONFIRMED,
  );
  const walletBalance = ledger?.creditBalance ?? 0;
  const walletCurrency =
    ledger?.currency ?? currentAssignment?.currency ?? "EGP";
  const formatAmount = (amount: number, currency = walletCurrency) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`.trim();
    }
  };

  const showWallet =
    isDefault || Boolean(currentAssignment && (isPerOrderCredit || walletBalance > 0));
  const showTopUps = isDefault || Boolean(currentAssignment);
  const topUpPanel = showTopUps ? (
    <StoreTopUpRequestsPanel storeId={storeId} locale={locale} requests={topUpRequests} t={t} />
  ) : null;

  const reversedNote = (payment: StoreSubscriptionPaymentView) =>
    [
      `${t(keys.admin.subscriptionPaymentReversedDetail)}: ${formatDate(payment.reversedAt, locale)}`,
      payment.reversedByAdmin?.name ?? null,
      payment.reversalReason ?? null,
    ]
      .filter(Boolean)
      .join(" · ");

  const paymentColumns: Array<Column<StoreSubscriptionPaymentView>> = [
    {
      key: "amount",
      label: t(keys.admin.subscriptionPaymentAmountLabel),
      width: 1.1,
      render: (payment) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span className="rd-num" style={{ fontWeight: 600 }}>
            {formatAmount(payment.amount, payment.currency)}
          </span>
          {payment.reversedAt ? (
            <span
              title={`${t(keys.admin.subscriptionPaymentReversedLabel)} · ${reversedNote(payment)}`}
              style={{ display: "inline-flex", cursor: "help" }}
            >
              <Icon
                icon={CircleAlert}
                size={15}
                color="var(--rd-red)"
                label={`${t(keys.admin.subscriptionPaymentReversedLabel)}: ${reversedNote(payment)}`}
              />
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "paid",
      label: t(keys.admin.subscriptionPaymentPaidAt),
      render: (payment) => formatDate(payment.paidAt, locale),
    },
    // A top-up closes no billing period, so there is no "next payment" to
    // name — the cell stays empty rather than a dash pretending to be a date.
    {
      key: "next",
      label: t(keys.admin.subscriptionPaymentNextAt),
      render: (payment) =>
        payment.scheduleDueDate ? (
          <span style={{ color: "var(--rd-text-secondary)" }}>{formatDate(payment.scheduleDueDate, locale)}</span>
        ) : null,
    },
    {
      key: "by",
      label: t(keys.admin.subscriptionPaymentSettledBy),
      render: (payment) => payment.settledByAdmin?.name ?? null,
    },
    {
      key: "actions",
      label: t(keys.common.actions),
      align: "end",
      width: 1.6,
      render: (payment) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {payment.proofObjectKey ? (
            <SubscriptionPaymentProofModal storeId={storeId} paymentId={payment.id} />
          ) : (
            <span style={{ fontSize: 12, color: "var(--rd-text-tertiary)" }}>
              {t(keys.admin.subscriptionPaymentProofMissing)}
            </span>
          )}
          {payment.reversedAt || !canReversePayments ? null : (
            <SubscriptionPaymentReverseButton
              storeId={storeId}
              paymentId={payment.id}
              amountLabel={formatAmount(payment.amount, payment.currency)}
            />
          )}
        </span>
      ),
    },
  ];

  const assignmentItems: TimelineItem[] = assignments
    .slice()
    .reverse()
    .map((assignment, index, list) => ({
      id: assignment.id,
      time: <span className="rd-num">{formatDate(assignment.startDate, locale)}</span>,
      tone: index === list.length - 1 ? "blue" : "ink",
      filled: true,
      label: pickLocalized(locale, assignment.planNameAr, assignment.planNameEn),
      note: [
        `${t(keys.admin.subscriptionAssignmentStartDate)}: ${formatDate(assignment.startDate, locale)}`,
        `${t(keys.admin.subscriptionAssignmentChangedBy)}: ${assignment.createdByAdmin.name}`,
      ].join(" · "),
      amount:
        assignment.perOrderAmount !== null ? (
          <span className="rd-num">
            {formatAmount(assignment.perOrderAmount, assignment.currency)}
          </span>
        ) : undefined,
    }));

  return (
    <div data-tab="subscriptions" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {isDefault || showWallet ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            gap: 12,
            alignItems: "start",
          }}
        >
          {isDefault ? (
            <StoreTierControls storeId={storeId} plans={plans} locale={locale} canManage={canReversePayments} />
          ) : null}
          {showWallet ? (
            <SectionCard
              badge="currency"
              title={t(keys.admin.subscriptionWalletTitle)}
              aside={
                canReversePayments ? (
                  <StorePaymentModal storeId={storeId} planType={isDefault ? "FREE" : currentAssignment?.type} />
                ) : null
              }
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                {/* Body ink at every balance: an empty wallet is stated by
                    the figure itself (and, per-order, by the hint below). */}
                <span className="rd-num" style={{ fontSize: 24, fontWeight: 700 }}>
                  {formatAmount(walletBalance)}
                </span>
                <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
                  {t(keys.admin.storeSubscriptionsWalletBalance)}
                </span>
              </div>
              {isPerOrderCredit && walletBalance <= 0 ? (
                <Meta>{t(keys.admin.subscriptionWalletEmptyHint)}</Meta>
              ) : null}
              {topUpPanel}
            </SectionCard>
          ) : null}
        </div>
      ) : null}

      {!showWallet && topUpPanel ? (
        <SectionCard badge="currency" title={t(keys.admin.subscriptionTopUpRequestsTitle)}>
          {topUpPanel}
        </SectionCard>
      ) : null}

      {afterPlan ?? null}

      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionHead
          title={t(keys.admin.subscriptionPaymentsTitle)}
          actions={
            canReversePayments && !showWallet ? (
              <StorePaymentModal storeId={storeId} planType={isDefault ? "FREE" : currentAssignment?.type} />
            ) : null
          }
        />
        <DataTable
          label={t(keys.admin.subscriptionPaymentsTitle)}
          columns={paymentColumns}
          rows={recordedPayments}
          rowKey={(payment) => payment.id}
          isDimmed={(payment) => Boolean(payment.reversedAt)}
          minWidth={640}
          empty={
            <TableEmpty title={t("admin.subscriptionPaymentsEmptyTitle")}>
              {t("admin.subscriptionPaymentsEmptyText")}
            </TableEmpty>
          }
        />
      </section>

      <section className="rd-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t(keys.admin.subscriptionAssignmentTitle)}</h2>
          <span style={{ flex: 1 }} />
          {!isDefault && canReversePayments ? (
            <StoreSubscriptionModal
              storeId={storeId}
              plans={plans.filter((plan) => plan.type !== "FREE" && plan.type !== "PREMIUM")}
              locale={locale}
            />
          ) : null}
        </div>
        {isDefault ? (
          <StoreLifecycleHistory entries={tierStatus.data?.lifecycleHistory ?? []} locale={locale} />
        ) : assignments.length === 0 ? (
          <EmptyView
            illustration="admin-billing"
            tone="blue"
            title={t(keys.admin.subscriptionAssignmentEmpty)}
            sub={t("admin.subscriptionHistoryEmptySub")}
          />
        ) : (
          <Timeline items={assignmentItems} />
        )}
      </section>
    </div>
  );
}
